import type {TextProjection} from './annotationTextProjection';
import type {AnnotationRange, TextAnchor} from './annotationTypes';

/** Context captured around `exact` for the deferred self-heal search. */
const PREFIX_SUFFIX_LENGTH = 32;

/**
 * Whether a range can become a mark: in bounds and covering real text — a drag
 * over just a collapsed newline is not a mark.
 */
export function isMarkableRange(
  projection: TextProjection,
  {start, end}: AnnotationRange
): boolean {
  if (start < 0 || end > projection.text.length || start >= end) {
    return false;
  }
  return projection.text.slice(start, end).trim() !== '';
}

/** Builds the frozen v1 anchor for a normalized range; null when not markable. */
export function buildAnchor(
  projection: TextProjection,
  {start, end}: AnnotationRange,
  {regionId, lang}: {regionId: string; lang: string | null}
): TextAnchor | null {
  if (!isMarkableRange(projection, {start, end})) {
    return null;
  }
  const exact = projection.text.slice(start, end);
  return {
    v: 1,
    regionId,
    lang,
    start,
    end,
    exact,
    prefix: projection.text.slice(Math.max(0, start - PREFIX_SUFFIX_LENGTH), start),
    suffix: projection.text.slice(end, end + PREFIX_SUFFIX_LENGTH)
  };
}

/**
 * Validate-or-drop: resolves iff the normalized text still reads `exact` at
 * `[start, end)` — never approximated. A drop is not a deletion; the anchor
 * resolves again when the DOM returns to its captured shape.
 */
export function resolveAnchor(
  anchor: TextAnchor,
  projection: TextProjection
): AnnotationRange | null {
  const {start, end, exact} = anchor;
  if (start < 0 || end <= start) {
    return null;
  }
  if (projection.text.slice(start, end) !== exact) {
    return null;
  }
  return {start, end};
}
